import { useState, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Container, Row, Col, Card, Spinner, Badge, Button } from 'react-bootstrap'
import { useAdminUser, useAdminUserUsage } from 'api/hooks/use-admin-user'
import AdminTabs from 'admin/shared/AdminTabs'
import AnalyticsDateFilter from '../analytics/AnalyticsDateFilter'
import UserChart from './UserChart'
import UserBarChart from './UserBarChart'
import UserActivitySummary from './UserActivitySummary'
import UserAiActivitySummary from './UserAiActivitySummary'
import UserLoginStats from './UserLoginStats'
import UserSessionTimeline from './UserSessionTimeline'
import UserSectionsManager from './UserSectionsManager'
import UserMunicipalitiesManager from './UserMunicipalitiesManager'
import UserInfoForm from './UserInfoForm'
import UserBlockCard from './UserBlockCard'
import UserStatusBadge from 'shared/badges/user-status-badge'

const computeDates = (months) => {
  const end = new Date()
  const start = new Date()
  start.setMonth(start.getMonth() - months)
  return {
    start_date: start.toISOString().split('T')[0],
    end_date: end.toISOString().split('T')[0]
  }
}

const StatCard = ({ title, value, isLoading }) => (
  <Card className="h-100">
    <Card.Body className="text-center">
      <Card.Title className="text-muted fs-6">{title}</Card.Title>
      <div className="fs-2 fw-bold">
        {isLoading ? <Spinner size="sm" /> : value?.toLocaleString() ?? '--'}
      </div>
    </Card.Body>
  </Card>
)

const UserSummaryCards = ({ userId, params }) => {
  const { data, isLoading } = useAdminUserUsage({ userId, endpoint: 'usage_summary', params })

  return (
    <Row className="mb-4">
      <Col md className="mb-3">
        <StatCard title="Visits" value={data?.total_visits} isLoading={isLoading} />
      </Col>
      <Col md className="mb-3">
        <StatCard title="Page Views" value={data?.total_page_views} isLoading={isLoading} />
      </Col>
      <Col md className="mb-3">
        <StatCard title="Events" value={data?.total_events} isLoading={isLoading} />
      </Col>
      <Col md className="mb-3">
        <StatCard title="Views Created" value={data?.dashboards_created} isLoading={isLoading} />
      </Col>
      <Col md className="mb-3">
        <StatCard title="Samples Created" value={data?.moles_created} isLoading={isLoading} />
      </Col>
      <Col md className="mb-3">
        <StatCard title="Exports" value={data?.exports_created} isLoading={isLoading} />
      </Col>
    </Row>
  )
}

const UserDetail = () => {
  const { id } = useParams()
  const { data: user, isLoading: userLoading } = useAdminUser({ userId: id })
  const [activeTab, setActiveTab] = useState('info')
  const [months, setMonths] = useState(1)
  const [grouping, setGrouping] = useState('day')

  const params = useMemo(() => {
    const dates = computeDates(months)
    return { ...dates, grouping }
  }, [months, grouping])

  if (userLoading) {
    return (
      <Container>
        <div className="text-center p-5">
          <Spinner animation="border" />
        </div>
      </Container>
    )
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="mb-0 d-flex align-items-center gap-2">
          {user?.first_name} {user?.last_name}
          {user?.is_admin && <Badge bg="danger" className="fs-6">Admin</Badge>}
          {user?.auth0_state && <UserStatusBadge state={user.auth0_state} className="fs-6" />}
        </h1>
        <div className="d-flex gap-2">
          <Button as={Link} to="/admin/users" variant="light" size="sm" className="text-primary">
            Back to Users
          </Button>
          <Button as={Link} to={`/admin/municipalities/${user?.municipality_id}`} variant="light" size="sm" className="text-primary">
            Back to Municipality
          </Button>
        </div>
      </div>

      {user?.municipality && (
        <div className="text-muted mb-3">
          {user.municipality.name} · {user.email}
        </div>
      )}

      {/* Login counts and the AI overview sit above the tabs: they are the
          answer to "who is this and are they using the product", which is what
          an admin opens this page for, whichever tab they end up on. */}
      <UserLoginStats loginStats={user?.login_stats} />

      <UserAiActivitySummary userId={id} />

      <AdminTabs
        tabs={[
          { name: 'Info', value: 'info' },
          { name: 'Sessions', value: 'sessions' },
          { name: 'Activity Summary', value: 'activity' }
        ]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {activeTab === 'info' && (
        <>
          <UserInfoForm user={user} />
          <UserBlockCard user={user} />
          {user?.user_type === 'accounting' && <UserMunicipalitiesManager user={user} />}
          <UserSectionsManager user={user} />
        </>
      )}

      {activeTab === 'sessions' && (
        <UserSessionTimeline userId={id} />
      )}

      {activeTab === 'activity' && (
        <>
          <UserActivitySummary userId={id} />

          <AnalyticsDateFilter
            months={months}
            grouping={grouping}
            onMonthsChange={setMonths}
            onGroupingChange={setGrouping}
          />

          <UserSummaryCards userId={id} params={params} />

          <Row className="mb-4">
            <Col lg={6} className="mb-4">
              <UserChart userId={id} title="Page Views Over Time" subtitle="Pages this user navigated to over time" endpoint="page_views_over_time" params={params} yAxisTitle="Page Views" color="#1E2E48" />
            </Col>
            <Col lg={6} className="mb-4">
              <UserChart userId={id} title="Visits Over Time" subtitle="Login sessions initiated by this user" endpoint="visits_over_time" params={params} yAxisTitle="Visits" color="#27AB83" />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={6} className="mb-4">
              <UserChart userId={id} title="Events Over Time" subtitle="Interactions such as clicks, exports, and filter changes" endpoint="events_over_time" params={params} yAxisTitle="Events" color="#E9C46A" />
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={6} className="mb-4">
              <UserBarChart userId={id} title="Top Pages" subtitle="Most visited pages ranked by view count" endpoint="top_pages" params={params} categoryKey="path" color="#E9C46A" />
            </Col>
            <Col lg={6} className="mb-4">
              <UserBarChart userId={id} title="Top Events" subtitle="Most frequent actions performed by this user" endpoint="top_events" params={params} categoryKey="name" color="#38BEC9" />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}

export default UserDetail
